import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Button,
  Stepper,
  Step,
  StepLabel,
  StepContent,
} from '@mui/material';
import {
  Update,
  School,
  ArrowBack,
  Article,
  CheckCircle,
  AttachMoney,
  Language,
  AccessTime,
  Home,
  MenuBook,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';

const StudentVisaGuidelines = () => {
  const theme = useTheme();

  const guidelines = [
    {
      title: 'Financial Requirements',
      icon: AttachMoney,
      details: [
        'Updated maintenance funds requirement',
        'New financial documentation guidelines',
        'Scholarship consideration policies',
      ],
    },
    {
      title: 'Language Requirements',
      icon: Language,
      details: [
        'IELTS/TOEFL score requirements',
        'English language test exemptions',
        'New assessment methods',
      ],
    },
    {
      title: 'Course Duration',
      icon: AccessTime,
      details: [
        'Maximum study period guidelines',
        'Course level restrictions',
        'Academic progression rules',
      ],
    },
    {
      title: 'Accommodation',
      icon: Home,
      details: [
        'Proof of accommodation requirements',
        'University housing options',
        'Private accommodation guidelines',
      ],
    },
  ];

  const applicationSteps = [
    {
      title: 'CAS Assignment',
      description: 'Obtain Confirmation of Acceptance for Studies from your chosen institution',
    },
    {
      title: 'Document Preparation',
      description: 'Gather all required documents including financial proof and qualifications',
    },
    {
      title: 'Visa Application',
      description: 'Complete the online application form and pay relevant fees',
    },
    {
      title: 'Biometrics',
      description: 'Attend appointment for biometric information collection',
    },
    {
      title: 'Decision',
      description: 'Wait for visa decision and prepare for travel upon approval',
    },
  ];

  return (
    <Box sx={{ py: { xs: 6, md: 8 } }}>
      <Container maxWidth="lg">
        {/* Header Section */}
        <Box sx={{ mb: 6 }}>
          <Button
            component={RouterLink}
            to="/"
            startIcon={<ArrowBack />}
            sx={{ mb: 3 }}
          >
            Back to Home
          </Button>
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '2rem', md: '3rem' },
              fontWeight: 700,
              mb: 2,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            New Student Visa Guidelines
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
            <Chip
              icon={<Update />}
              label="March 10, 2024"
              sx={{ backgroundColor: `${theme.palette.primary.main}15` }}
            />
            <Chip
              icon={<School />}
              label="Student Visa"
              sx={{ backgroundColor: `${theme.palette.secondary.main}15` }}
            />
          </Box>
          <Typography
            variant="h5"
            color="textSecondary"
            sx={{ mb: 4, maxWidth: 800 }}
          >
            Updated guidelines for international students planning to study in the UK, including new requirements and application procedures.
          </Typography>
          <Divider sx={{ my: 4 }} />
        </Box>

        {/* Main Content */}
        <Grid container spacing={4}>
          {/* Key Guidelines */}
          {guidelines.map((guideline, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  height: '100%',
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <guideline.icon
                    sx={{
                      fontSize: 40,
                      color: theme.palette.primary.main,
                      mr: 2,
                    }}
                  />
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {guideline.title}
                  </Typography>
                </Box>
                <List>
                  {guideline.details.map((detail, idx) => (
                    <ListItem key={idx} sx={{ px: 0 }}>
                      <ListItemIcon>
                        <CheckCircle color="primary" sx={{ fontSize: 20 }} />
                      </ListItemIcon>
                      <ListItemText primary={detail} />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Application Process Steps */}
        <Paper sx={{ mt: 6, p: 4 }}>
          <Typography
            variant="h4"
            gutterBottom
            sx={{
              fontWeight: 600,
              mb: 4,
              color: theme.palette.primary.main,
            }}
          >
            Application Process
          </Typography>
          <Stepper orientation="vertical">
            {applicationSteps.map((step, index) => (
              <Step key={index} active={true}>
                <StepLabel
                  StepIconProps={{
                    sx: {
                      color: theme.palette.primary.main,
                    },
                  }}
                >
                  <Typography variant="h6">{step.title}</Typography>
                </StepLabel>
                <StepContent>
                  <Typography color="textSecondary" sx={{ mt: 1 }}>
                    {step.description}
                  </Typography>
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </Paper>

        {/* Call to Action */}
        <Paper
          sx={{
            mt: 6,
            p: 4,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}15, ${theme.palette.primary.light}15)`,
            borderRadius: 2,
          }}
        >
          <Typography variant="h5" gutterBottom>
            Ready to Start Your UK Education Journey?
          </Typography>
          <Typography paragraph color="textSecondary">
            Our education specialists can help you navigate the student visa application process and ensure you meet all the new requirements.
          </Typography>
          <Button
            component={RouterLink}
            to="/consultation"
            variant="contained"
            color="primary"
            size="large"
            sx={{ mt: 2 }}
          >
            Get Expert Guidance
          </Button>
        </Paper>
      </Container>
    </Box>
  );
};

export default StudentVisaGuidelines; 